// MindForge web — TS port of state.py / PlayerState.swift.
//
// Mutable class — mirrors the Python and Swift implementations exactly so
// the three platforms can be debugged with the same mental model. React
// components wrap an instance via usePlayerState() which re-renders on
// any mutation.

import {
  MODE_MAX_DIFFICULTY,
  MODE_START_DIFFICULTY,
  MODE_UNLOCKS,
} from "./modeConfig";
import { levelFromXp, xpForLevel, xpProgress } from "../logic/scoring";

/** Bump when the JSON save schema changes. v1 had no version/settings; v2 adds both. */
export const SAVE_VERSION = 2;

export class PlayerState {
  totalXp: number = 0;
  modeDifficulty: Record<string, number> = { ...MODE_START_DIFFICULTY };
  modeHighScores: Record<string, number> = {};
  sessionsPlayed: number = 0;
  soundEnabled: boolean = true;

  /** Reset all progress to initial state (preserves the sound setting). */
  reset(): void {
    const sound = this.soundEnabled;
    this.totalXp = 0;
    this.modeDifficulty = { ...MODE_START_DIFFICULTY };
    this.modeHighScores = {};
    this.sessionsPlayed = 0;
    this.soundEnabled = sound;
  }

  // ─── Computed properties ────────────────────────────────────────────

  get level(): number {
    return levelFromXp(this.totalXp);
  }

  get xpForCurrentLevel(): number {
    return xpForLevel(this.level);
  }

  get xpForNextLevel(): number {
    return xpForLevel(this.level + 1);
  }

  get xpProgressFrac(): number {
    return xpProgress(this.totalXp);
  }

  // ─── Mode queries ────────────────────────────────────────────────────

  isUnlocked(modeId: string): boolean {
    const required = MODE_UNLOCKS[modeId] ?? 999;
    return this.level >= required;
  }

  getUnlockedModes(): string[] {
    return Object.entries(MODE_UNLOCKS)
      .filter(([, lv]) => this.level >= lv)
      .map(([id]) => id);
  }

  getModeDifficulty(modeId: string): number {
    return (
      this.modeDifficulty[modeId] ?? MODE_START_DIFFICULTY[modeId] ?? 1
    );
  }

  increaseDifficulty(modeId: string): void {
    // Clamp to the per-mode ceiling — auto-promotion (80%+ accuracy) must not
    // bypass the cap that the load path and the difficulty tuner already
    // enforce. Mirrors state.py / PlayerState.swift.
    const cap = MODE_MAX_DIFFICULTY[modeId] ?? 15;
    this.modeDifficulty[modeId] = Math.min(
      this.getModeDifficulty(modeId) + 1,
      cap,
    );
  }

  // ─── XP ──────────────────────────────────────────────────────────────

  /** Subtract XP, clamped to 0. Does NOT cause a level-down. */
  deductXp(amount: number): void {
    if (amount <= 0) return;
    this.totalXp = Math.max(0, this.totalXp - amount);
  }

  /** Add XP and return the list of newly unlocked mode IDs. */
  addXp(amount: number): string[] {
    if (amount <= 0) return [];
    const oldLevel = this.level;
    this.totalXp += amount;
    const newLevel = this.level;
    if (newLevel <= oldLevel) return [];

    const unlocks: string[] = [];
    for (const [modeId, reqLevel] of Object.entries(MODE_UNLOCKS)) {
      if (oldLevel < reqLevel && reqLevel <= newLevel) {
        unlocks.push(modeId);
      }
    }
    return unlocks;
  }

  updateHighScore(modeId: string, score: number): void {
    if (score > (this.modeHighScores[modeId] ?? 0)) {
      this.modeHighScores[modeId] = score;
    }
  }

  // ─── Save schema (versioned JSON) ────────────────────────────────────

  /** Serialize to a plain object suitable for JSON.stringify. */
  toJSON(): SavePayload {
    return {
      version: SAVE_VERSION,
      total_xp: this.totalXp,
      mode_difficulty: { ...this.modeDifficulty },
      mode_high_scores: { ...this.modeHighScores },
      sessions_played: this.sessionsPlayed,
      settings: {
        sound_enabled: this.soundEnabled,
      },
    };
  }

  /**
   * Inverse of toJSON. Validates everything — corrupt fields fall back to
   * defaults instead of crashing. Forward-compatible with v1 saves
   * (no version key, no settings block).
   */
  static fromJSON(raw: unknown): PlayerState {
    const state = new PlayerState();
    if (!isPlainObject(raw)) return state;

    const totalXp = raw.total_xp;
    if (typeof totalXp === "number" && totalXp >= 0 && Number.isInteger(totalXp)) {
      state.totalXp = totalXp;
    }

    const savedDiff = raw.mode_difficulty;
    if (isPlainObject(savedDiff)) {
      const valid: Record<string, number> = {};
      for (const [mid, val] of Object.entries(savedDiff)) {
        if (
          mid in MODE_START_DIFFICULTY &&
          typeof val === "number" &&
          Number.isInteger(val) &&
          val >= 1
        ) {
          valid[mid] = Math.min(val, MODE_MAX_DIFFICULTY[mid] ?? 15);
        }
      }
      state.modeDifficulty = { ...MODE_START_DIFFICULTY, ...valid };
    }

    const savedScores = raw.mode_high_scores;
    if (isPlainObject(savedScores)) {
      const valid: Record<string, number> = {};
      for (const [mid, val] of Object.entries(savedScores)) {
        if (
          typeof mid === "string" &&
          mid in MODE_START_DIFFICULTY &&
          typeof val === "number" &&
          Number.isInteger(val) &&
          val >= 0
        ) {
          valid[mid] = val;
        }
      }
      state.modeHighScores = valid;
    }

    const sp = raw.sessions_played;
    if (typeof sp === "number" && Number.isInteger(sp) && sp >= 0) {
      state.sessionsPlayed = sp;
    }

    const version = typeof raw.version === "number" ? raw.version : 1;
    if (version >= 2 && isPlainObject(raw.settings)) {
      const se = (raw.settings as Record<string, unknown>).sound_enabled;
      state.soundEnabled = typeof se === "boolean" ? se : true;
    }

    return state;
  }
}

// ─── Internal helpers ──────────────────────────────────────────────────

export interface SavePayload {
  version: number;
  total_xp: number;
  mode_difficulty: Record<string, number>;
  mode_high_scores: Record<string, number>;
  sessions_played: number;
  settings: {
    sound_enabled: boolean;
  };
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
